import { StreamingPlayerImage, StreamingPlayerTypography } from '@heardis/stream-contracts';
import { useEffect } from 'react';
import { MediaStatus, PlaybackStatus, usePlayerContext } from '../../contexts/PlayerContext';
import PauseButton from '../../elements/PauseButton';
import PlayButton from '../../elements/Playbutton';
import SkipToLastButton from '../../elements/SkipLastButton';
import SkipToNextButton from '../../elements/SkipToNextButton';
import SpotifyButton from '../../elements/SpotifyButton';
import { AnalyticsEventType } from '../../hooks/analytics/analytics.types';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { getArtworkUri } from '../../utils/artwork';
import { Logger } from '../../utils/logger';
import { classNames } from '../../utils/styles';
import { formatTimeInMMSS } from '../../utils/time';
import { Artwork, BackgroundImage } from '../Image';
import classes from './WidgetPlayer.module.css';
import { PlayerAction, PlayerActionType } from './player.types';

const logger = new Logger('Player');

interface PlayerProps {
  vertical?: boolean;
  typography: StreamingPlayerTypography;
  elementsColor: string;
  backgroundColor: string;
  backgroundImage: StreamingPlayerImage;
  artwork: {
    enabled: boolean;
    alwaysDefault: boolean;
    defaultImage: StreamingPlayerImage;
  };
  spotifyEnabled: boolean;
  controlFeatureLeft?: React.ReactNode;
  controlFeatureRight?: React.ReactNode;
}

function WidgetPlayer({ vertical, typography, elementsColor, backgroundColor, backgroundImage, artwork, spotifyEnabled, controlFeatureLeft, controlFeatureRight }: PlayerProps): JSX.Element {
  const playerContext = usePlayerContext();
  const { playbackStatus, currentTime, duration, mediaStatus, canSeek, currentTrack } = playerContext;
  const { trackAnalyticsEvent } = useAnalytics();
  const isNotReadyToPlay = [MediaStatus.LOADING, MediaStatus.ERROR, MediaStatus.NO_MEDIA].includes(mediaStatus);
  const isPlaying = playbackStatus === PlaybackStatus.PLAYING;
  const progress = `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%`;
  const { fontFamily, fontSize, fontWeight, color } = typography;

  const handleUserInteraction = (action: PlayerAction) => {
    switch (action.type) {
      case PlayerActionType.SEEK:
        playerContext.seek(action.position);
        break;
      case PlayerActionType.PLAY:
        playerContext.play();
        break;
      case PlayerActionType.PAUSE:
        playerContext.pause();
        break;
      case PlayerActionType.PLAY_PREVIOUS:
        playerContext.previous?.();
        break;
      case PlayerActionType.PLAY_NEXT:
        playerContext.next?.();
        break;
      case PlayerActionType.LINK_TO_SPOTIFY:
        trackAnalyticsEvent({ type: AnalyticsEventType.LINK_TO_SPOTIFY, track_id: action.trackId });
        break;
      default:
        logger.warn('Unhandled action:', action);
    }
  };

  /** @TODO add React.MouseEvent<HTMLDivElement, DragEvent> to handle dragging the circle */
  const handleProgressbarClick = (event: React.MouseEvent<HTMLDivElement, PointerEvent>) => {
    if (!duration || !playerContext.seek) return;

    const progressbarWidth = event.currentTarget.offsetWidth;
    const clickPositionX = event.nativeEvent.offsetX;
    const seekPosition = duration * (clickPositionX / progressbarWidth);
    handleUserInteraction({ type: PlayerActionType.SEEK, position: seekPosition });
  };

  useEffect(() => {
    if ('mediaSession' in navigator && currentTrack) {
      window.navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentTrack.track.title,
        artist: currentTrack.track.artist,
        album: '',
        artwork: [
          { src: getArtworkUri(artwork.defaultImage.url, currentTrack?.track.artworkUri, artwork.alwaysDefault) },
        ],
      });
    }
  }, [currentTrack?.track.id]);

  return (
    <div className={classNames(classes.player, vertical ? classes.vertical : '')} style={{ fontFamily, fontSize, fontWeight, color, backgroundColor }}>
      {artwork.enabled && (
      <div className={classes.artworkContainer}>
        <Artwork defaultArtwork={artwork.defaultImage} alwaysDefaultEnabled={artwork.alwaysDefault} track={currentTrack?.track} />
      </div>
      )}
      <div className={classNames(classes.playerDetailsContainer, vertical ? classes.vertical : '')}>
        <div className={classes.playerDetailsWrapper}>
          <section className={classes.trackInfoContainer} style={{ color }}>
            <div>
              <p className={classes.trackInfoTitle}>{currentTrack?.track.title ? currentTrack.track.title : 'Unknown title'}</p>
              <p className={classes.trackInfoArtist}>{currentTrack?.track.artist ? currentTrack.track.artist : 'Unknown artist'}</p>
            </div>
            {spotifyEnabled && currentTrack?.track.spotifyId && (
            <SpotifyButton
              svgProps={{ fill: color, width: '24', height: '24' }}
              spotifyId={currentTrack.track.spotifyId}
              onClick={() => handleUserInteraction({ type: PlayerActionType.LINK_TO_SPOTIFY, trackId: currentTrack.track.spotifyId! })}
            />
            )}
          </section>
          <section className={classNames(classes.progressBarContainer, isNotReadyToPlay ? classes.disabled : '')}>
            <div className={classes.progressBarWrapper}>
              <div className={classes.progressBar} onClick={handleProgressbarClick}>
                <div className={classNames(classes.progressBarBackground, classes.roundedBorder)} style={{ backgroundColor: elementsColor, cursor: canSeek ? 'pointer' : undefined }} />
                <div className={classNames(classes.progress, classes.roundedBorder)} style={{ width: progress, backgroundColor: elementsColor }} />
              </div>
              {canSeek && (
                <div className={classes.progress} style={{ width: progress }}>
                  <div className={classes.progressDragCircle} style={{ backgroundColor: elementsColor }} />
                </div>
              )}
            </div>
            <div className={classNames(classes.times)} style={{ color: elementsColor }}>
              <p>{formatTimeInMMSS(currentTime)}</p>
              <p>{duration ? formatTimeInMMSS(duration) : '--/--'}</p>
            </div>
          </section>
          <section className={classes.playbackControlsContainer}>
            <div className={classes.playbackControlWrapper}>
              {controlFeatureLeft}
            </div>
            <div className={classes.playbackControlWrapper}>
              {playerContext.previous && <SkipToLastButton className={classNames(classes.playbackControlElement, classes.skipButton)} fill={elementsColor} onClick={() => handleUserInteraction({ type: PlayerActionType.PLAY_PREVIOUS })} />}
              {isPlaying ?
                <PauseButton className={classNames(classes.playbackControlElement, classes.playPauseButton)} fill={elementsColor} onClick={() => handleUserInteraction({ type: PlayerActionType.PAUSE })} /> :
                <PlayButton className={classNames(classes.playbackControlElement, classes.playPauseButton, isNotReadyToPlay ? classes.disabled : '')} fill={elementsColor} onClick={() => handleUserInteraction({ type: PlayerActionType.PLAY })} />}
              {playerContext.next && <SkipToNextButton className={classNames(classes.playbackControlElement, classes.skipButton)} fill={elementsColor} onClick={() => handleUserInteraction({ type: PlayerActionType.PLAY_NEXT })} />}
            </div>
            <div className={classes.playbackControlWrapper}>
              {controlFeatureRight}
            </div>
          </section>
        </div>
      </div>
      {backgroundImage.url && (
        <BackgroundImage
          url={backgroundImage.url}
          alt={backgroundImage.alt}
          blur={backgroundImage.blur}
          position={backgroundImage.position}
          fit={backgroundImage.fit}
          zIndex={-5}
        />
      )}
    </div>
  );
}

export default WidgetPlayer;
